/**
 * Parse `search_for_information_in_source_of_knowledge` tool results and group
 * snippets by source document ("page") for Kurama `searchDetails`.
 */
import { type CopilotSearchSourcePage } from '@precis-digital/kurama/MessageLayout'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Some AG-UI / ADK paths wrap the tool dict under `result`. */
const unwrapToolResult = (parsed: unknown): Record<string, unknown> | null => {
  if (!isRecord(parsed)) {
    return null
  }
  const inner = parsed.result
  if (isRecord(inner)) {
    return inner
  }
  return parsed
}

const parseToolResultPayload = (content: string | undefined): Record<string, unknown> | null => {
  if (content == null || content.trim().length === 0) {
    return null
  }
  try {
    return unwrapToolResult(JSON.parse(content) as unknown)
  } catch {
    return null
  }
}

/**
 * Unique source pages from knowledge_snippets (order of first appearance).
 * Returns [] when the result is missing, errored, or has no snippets.
 */
export const groupKnowledgeSearchSources = (toolResultContent: string | undefined): CopilotSearchSourcePage[] => {
  const payload = parseToolResultPayload(toolResultContent)
  if (payload == null || payload.status === 'error') {
    return []
  }

  const snippets = payload.knowledge_snippets
  if (!Array.isArray(snippets) || snippets.length === 0) {
    return []
  }

  const pages: CopilotSearchSourcePage[] = []
  const seenTitles = new Set<string>()

  for (const raw of snippets) {
    if (!isRecord(raw)) {
      continue
    }
    const text = typeof raw.text === 'string' ? raw.text.trim() : ''
    if (text.length === 0) {
      continue
    }
    const title = typeof raw.source === 'string' && raw.source.trim().length > 0 ? raw.source.trim() : 'Untitled source'
    if (seenTitles.has(title)) {
      continue
    }
    seenTitles.add(title)
    pages.push({ title })
  }

  return pages
}
